import useFlag, { FLAGS } from "@coworker/app/src/hooks/useFlag";
import { useBurstDebouncedCallback } from "@coworker/apprestructured/src/shared/hooks/useBurstDebouncedCallback";
import { Item } from "@coworker/apprestructured/src/shared/types/item";
import { FixaSearchActions } from "@coworker/apprestructured/src/shared/wrappers/FixaSearch/FixaSearch";
import { useScannerInputDetection } from "@ingka-group-digital/flexa-scanner";
import React, { useState } from "react";
import { SearchItemCardRenderType } from "../../types/SearchItemCardRenderType";
import { ProductSearchPagePresentation } from "./ProductSearchPagePresentation";

const SEARCH_QUERY_UPDATE_DELAY_MS = 50;

interface ProductSearchComponentProps {
  searchQuery: string;
  setSearchQuery: React.Dispatch<React.SetStateAction<string>>;
  handleItemCardClick: (item: Item) => void;
  itemCardRenderType: SearchItemCardRenderType;
  shouldHideSPRItems: boolean;
  shouldHideLocalCombinationItems: boolean;
  shouldHideHistory?: boolean;
  shouldHideResults?: boolean;
  searchBarActions?: FixaSearchActions[];
  autoFocus?: boolean;
  onSearchFocus?: () => void;
  onSearchBlur?: () => void;
  multiSelect?: boolean;
  selectedItemNos?: string[];
  onToggleItemSelect?: (item: Item) => void;
}

export const ProductSearchComponent = ({
  searchQuery,
  setSearchQuery,
  handleItemCardClick,
  itemCardRenderType,
  shouldHideSPRItems,
  shouldHideLocalCombinationItems,
  shouldHideHistory = false,
  shouldHideResults = false,
  searchBarActions,
  autoFocus = true,
  onSearchFocus,
  onSearchBlur,
  multiSelect = false,
  selectedItemNos = [],
  onToggleItemSelect,
}: ProductSearchComponentProps) => {
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [isInteractingWithHistory, setIsInteractingWithHistory] =
    useState(false);

  const { handleInputChange } = useScannerInputDetection(
    searchQuery.length,
    (articleNumber) => setSearchQuery(articleNumber)
  );

  const updateSearchQuery = (value: string) => {
    setSearchQuery(value);
    handleInputChange(value);
  };

  // Problem: with autoFocus on, a HID barcode scanner (keyboard-wedge) can flush an
  // entire scanned code into this input as dozens of synchronous native "input" events
  // right after mount, before React ever gets a chance to paint. Each event used to call
  // setSearchQuery synchronously, and each of those triggers a heavy re-render (SearchResults
  // re-queries via react-query). Because none of these updates get a chance to commit/paint
  // in between, React's internal nested-update counter for the root keeps incrementing across
  // the whole burst and eventually exceeds its limit, throwing
  // "Maximum update depth exceeded" (see Search.js's handleValueChange in the stack trace).
  // Fix: debounce the state update + scanner detection so a fast burst of keystrokes collapses
  // into a single update after SEARCH_QUERY_UPDATE_DELAY_MS, giving React room to commit/paint
  // between bursts instead of piling up dozens of synchronous renders.
  const {
    debouncedCallback: debouncedHandleSearchQueryChange,
    cancel: cancelDebouncedHandleSearchQueryChange,
  } = useBurstDebouncedCallback(
    updateSearchQuery,
    SEARCH_QUERY_UPDATE_DELAY_MS
  );

  const burstDebouncedEnabled = useFlag(FLAGS.ENABLE_BURST_DEBOUNCED);
  const handleSearchQueryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (burstDebouncedEnabled) {
      debouncedHandleSearchQueryChange(e.target.value);
    } else {
      updateSearchQuery(e.target.value);
    }
  };

  const handleSearchQueryReset = () => {
    cancelDebouncedHandleSearchQueryChange();
    setSearchQuery("");
  };

  const handleSetSearchQuery: React.Dispatch<React.SetStateAction<string>> = (
    value
  ) => {
    cancelDebouncedHandleSearchQueryChange();
    setSearchQuery(value);
  };

  const handleSearchBlur = () => {
    if (!isInteractingWithHistory) {
      setIsSearchFocused(false);
      onSearchBlur?.();
    }
  };

  const handleSearchFocus = () => {
    setIsSearchFocused(true);
    onSearchFocus?.();
  };

  return (
    <ProductSearchPagePresentation
      shouldHideHistory={shouldHideHistory}
      shouldHideResults={shouldHideResults}
      itemCardRenderType={itemCardRenderType}
      searchQuery={searchQuery || ""}
      setSearchQuery={handleSetSearchQuery}
      handleItemCardClick={handleItemCardClick}
      shouldHideSPRItems={shouldHideSPRItems}
      shouldHideLocalCombinationItems={shouldHideLocalCombinationItems}
      searchBarActions={searchBarActions}
      autoFocus={autoFocus}
      isSearchFocused={isSearchFocused}
      isInteractingWithHistory={isInteractingWithHistory}
      setIsInteractingWithHistory={setIsInteractingWithHistory}
      handleSearchQueryChange={handleSearchQueryChange}
      handleSearchQueryReset={handleSearchQueryReset}
      handleSearchBlur={handleSearchBlur}
      handleSearchFocus={handleSearchFocus}
      multiSelect={multiSelect}
      selectedItemNos={selectedItemNos}
      onToggleItemSelect={onToggleItemSelect}
    />
  );
};
